import api from './api';
import { User, PersonalData, CardDTO, AddCardResponse, FnsAuthResult } from '../types';
import { API_ENDPOINTS, STORAGE_KEYS } from '../constants/api';

class UserService {
  // Получение информации о текущем пользователе
  async getCurrentUser(): Promise<User> {
    try {
      const response = await api.get(API_ENDPOINTS.USER.PROFILE);
      return response.data as User;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  // Обновление профиля пользователя
  async updateProfile(userData: Partial<User>): Promise<User> {
    try {
      const response = await api.put(API_ENDPOINTS.USER.PROFILE, userData);
      return response.data as User;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Обновление персональных данных
  async updatePersonalData(personalData: Partial<PersonalData>): Promise<User> {
    try {
      const response = await api.put(API_ENDPOINTS.USER.PERSONAL_DATA, personalData);
      return response.data as User;
    } catch (error) {
      console.error('Error updating personal data:', error);
      throw error;
    }
  }

  // Получение персональных данных
  async getPersonalData(): Promise<PersonalData | null> {
    try {
      const response = await api.get(API_ENDPOINTS.USER.PERSONAL_DATA);
      return response.data as PersonalData;
    } catch (error) {
      console.error('Error fetching personal data:', error);
      return null;
    }
  }

  // Включение/выключение ФНС
  async toggleFnsEnabled(enabled: boolean): Promise<User> {
    try {
      const response = await api.post(API_ENDPOINTS.USER.FNS_TOGGLE, { isFnsEnabled: enabled });
      return response.data as User;
    } catch (error) {
      console.error('Error toggling FNS:', error);
      throw error;
    }
  }

  // Инициация подключения "Мой налог"
  async initiateFnsAuth(): Promise<FnsAuthResult> {
    try {
      const response = await api.post(API_ENDPOINTS.USER.FNS_AUTH);
      return response.data as FnsAuthResult;
    } catch (error) {
      console.error('Error initiating FNS auth:', error);
      throw error;
    }
  }

  // Подтверждение кода из SMS для "Мой налог"
  async verifyFnsCode(challengeToken: string, code: string): Promise<void> {
    try {
      await api.post(API_ENDPOINTS.USER.FNS_VERIFY, { challengeToken, code });
    } catch (error) {
      console.error('Error verifying FNS code:', error);
      throw error;
    }
  }

  // Подтверждение по телефону через MTS
  async verifyByPhone(birthday: string): Promise<void> {
    try {
      await api.post(API_ENDPOINTS.USER.MTS, { birthDay: birthday });
    } catch (error) {
      console.error('Error verifying by phone:', error);
      throw error;
    }
  }

  // Подтверждение SMS кода от MTS
  async verifyMtsCode(code: string): Promise<void> {
    try {
      await api.post(API_ENDPOINTS.USER.MTS_VERIFY, { code });
    } catch (error) {
      console.error('Error verifying MTS code:', error);
      throw error;
    }
  }

  // Инициация сканирования паспорта через RIM
  async initiateRimScan(): Promise<{ url?: string }> {
    try {
      const response = await api.post(API_ENDPOINTS.USER.RIM);
      return response.data as { url?: string };
    } catch (error) {
      console.error('Error initiating RIM scan:', error);
      throw error;
    }
  }

  // Подтверждение сканирования паспорта через RIM
  async verifyRimScan(): Promise<void> {
    try {
      await api.post(API_ENDPOINTS.USER.RIM_VERIFY);
    } catch (error) {
      console.error('Error verifying RIM scan:', error);
      throw error;
    }
  }

  // Получение списка банковских карт
  async getCards(): Promise<CardDTO[]> {
    try {
      const response = await api.get(API_ENDPOINTS.USER.CARDS);
      return response.data as CardDTO[];
    } catch (error) {
      console.error('Error fetching cards:', error);
      return [];
    }
  }

  // Добавление новой банковской карты
  async addCard(): Promise<AddCardResponse> {
    try {
      const response = await api.post(API_ENDPOINTS.USER.CARD_ADD);
      return response.data as AddCardResponse;
    } catch (error) {
      console.error('Error adding card:', error);
      throw error;
    }
  }

  // Удаление банковской карты
  async removeCard(cardId: string): Promise<boolean> {
    try {
      await api.delete(`${API_ENDPOINTS.USER.CARD_DELETE}/${cardId}/delete`);
      return true;
    } catch (error) {
      console.error('Error removing card:', error);
      return false;
    }
  }
  
  // Сохранение пользователя в localStorage
  saveUserToStorage(user: User): void {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  }

  // Получение пользователя из localStorage
  getUserFromStorage(): User | null {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (userData) {
      try {
        return JSON.parse(userData) as User;
      } catch (error) {
        console.error('Error parsing user data from storage:', error);
        return null;
      }
    }
    return null;
  }

  // Очистка данных пользователя из localStorage
  clearUserFromStorage(): void {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }
}

export const userService = new UserService();
export default userService;
